import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { lazy, Suspense, useEffect } from 'react';
import Navigation from "@/components/navigation";
import ScrollToTop from "@/components/scroll-to-top";
import { Helmet } from "react-helmet-async";
import { initGA } from "./lib/analytics";
import { useAnalytics } from "./hooks/use-analytics";

// Eagerly loaded core pages
import Home from "@/pages/home";
import NotFound from "@/pages/not-found";

// Lazily loaded pages to reduce initial bundle size
const FutureYou = lazy(() => import("@/pages/future-you"));
const ThankYou = lazy(() => import("@/pages/thank-you"));
const Contact = lazy(() => import("@/pages/contact"));
const PrivacyPolicy = lazy(() => import("@/pages/privacy-policy"));
const Tools = lazy(() => import("@/pages/tools"));
const Quiz = lazy(() => import("@/pages/quiz"));
const Blog = lazy(() => import("@/pages/blog"));
const StructuredDataPage = lazy(() => import("@/pages/structured-data"));



// Lazily loaded blog posts - these are rarely needed on first load
const PsychologyImpulseSpendingControl = lazy(() => import("@/pages/blog-posts/psychology-impulse-spending-control"));
const FomoSpendingPsychology = lazy(() => import("@/pages/blog-posts/fomo-spending-psychology"));
const PersonalityTypeSpendingHabits = lazy(() => import("@/pages/blog-posts/personality-type-spending-habits"));
const MicroHabitsSaveMoney = lazy(() => import("@/pages/blog-posts/micro-habits-save-money"));
const WhyWeImpulseBuyAndHowToStop = lazy(() => import("@/pages/blog-posts/why-we-impulse-buy-and-how-to-stop"));
const BestAppCurbImpulseSpending = lazy(() => import("@/pages/blog-posts/best-app-curb-impulse-spending"));
const FinancialBehaviorsLiteracy = lazy(() => import("@/pages/blog/financial-behaviors-literacy"));
const HealthBenefitsBudgeting = lazy(() => import("@/pages/blog-posts/health-benefits-budgeting"));
const PsychologyOfMoneyReview = lazy(() => import("@/pages/blog-posts/psychology-of-money-review"));
const WhatDrivesImpulseSpendingResearch = lazy(() => import("@/pages/blog-posts/what-drives-impulse-spending-research"));
const CompleteGuideStoppingImpulseSpending = lazy(() => import("@/pages/blog-posts/complete-guide-stopping-impulse-spending"));
const PsychologyImpulseSpendingScience = lazy(() => import("@/pages/blog-posts/psychology-impulse-spending-science"));
const EmotionalTriggersImpulseSpending = lazy(() => import("@/pages/blog-posts/emotional-triggers-impulse-spending"));
const CBTTechniquesImpulseSpending = lazy(() => import("@/pages/blog-posts/cbt-techniques-impulse-spending"));

// Enhanced JSON-LD structured data component
function StructuredDataSchema() {
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "impause",
    alternateName: "impause - Transform Your Financial Mindset",
    url: "https://impause.com",
    description:
      "Transform your relationship with money through psychology-backed insights. Stop impulse spending, build mindful habits, and achieve financial wellness.",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://impause.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "impause",
    url: "https://impause.com",
    logo: "https://impause.com/images/social/Meta_image.png",
    sameAs: [
      "https://twitter.com/impauseapp",
      "https://www.instagram.com/impauseapp/",
      "https://www.linkedin.com/company/impause",
    ],
    description:
      "Transform your relationship with money through psychology-backed insights. Stop impulse spending, build mindful habits, and achieve financial wellness.",
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
    </>
  );
}

// Add type for SEO props
interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  path: string;
  robotsContent?: string;
}

// SEO component for managing meta tags
function SEO({ title, description, canonicalUrl, path, robotsContent = "index, follow" }: SEOProps) {
  const defaultTitle = "impause | Build Better Money Habits";
  const defaultDescription =
    "Master your money mindset. Stop impulse spending. Start feeling in control. Welcome to impause.";
  const siteUrl = "https://impause.com";

  const fullTitle = title ? `${title}` : defaultTitle;
  const metaDescription = description || defaultDescription;
  const canonical = canonicalUrl || `${siteUrl}${path || ""}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* Canonical Link */}
      <link rel="canonical" href={canonical} />

      {/* Robots Meta Tag */}
      <meta name="robots" content={robotsContent} />

      {/* Open Graph Tags */}
      <meta property="og:title" content="impause | Build Better Money Habits" />
      <meta property="og:description" content="Master your money mindset. Stop impulse spending. Start feeling in control. Welcome to impause." />
      <meta property="og:image" content="https://impause.com/images/social/Meta_image.png" />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="impause" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="impause | Build Better Money Habits" />
      <meta name="twitter:description" content="Master your money mindset. Stop impulse spending. Start feeling in control." />
      <meta name="twitter:image" content="https://impause.com/images/social/Meta_image.png" />
      
      {/* Apple-specific meta tags for iMessage links and iOS display */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="impause" />
      <link rel="apple-touch-icon" href="/images/social/Meta_image.png" />
      <link rel="apple-touch-startup-image" href="/images/social/og/impause-for-imessage.png" />
      <meta name="apple-itunes-app" content="app-id=6476052512" />
      <meta property="al:ios:app_name" content="impause" />
      <meta property="al:ios:app_store_id" content="6476052512" />

      {/* Additional SEO tags */}
      <meta
        name="keywords"
        content="impulse spending, stop spending, financial anxiety, personal finance, behavioral finance, financial wellness, money mindset"
      />
      
      {/* Preload critical images - WebP versions */}
      <link rel="preload" as="image" href="/images/optimized/Impause app (Copy) (2)-320.webp" type="image/webp" media="(max-width: 320px)" />
      <link rel="preload" as="image" href="/images/optimized/Impause app (Copy) (2)-640.webp" type="image/webp" media="(min-width: 321px) and (max-width: 640px)" />
      <link rel="preload" as="image" href="/images/optimized/Impause app (Copy) (2)-1024.webp" type="image/webp" media="(min-width: 641px) and (max-width: 1024px)" />
      <link rel="preload" as="image" href="/images/optimized/Impause app (Copy) (2)-2048.webp" type="image/webp" media="(min-width: 1025px)" />
      
      {/* Preload critical images - Fallback versions */}
      <link rel="preload" as="image" href="/images/optimized/Impause app (Copy) (2)-640.png" type="image/png" media="(max-width: 640px)" />
      <link rel="preload" as="image" href="/images/optimized/Impause app (Copy) (2)-1024.png" type="image/png" media="(min-width: 641px)" />
      
      {/* Preload critical fonts */}
      <link
        rel="preconnect"
        href="https://fonts.googleapis.com"
        crossOrigin="anonymous"
      />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
    </Helmet>
  );
}

function Router() {
  // Track page views when routes change
  useAnalytics();
  
  return (
    <>
      <ScrollToTop />
      <StructuredDataSchema />
      <Navigation />
      <Routes>
          <Route 
            path="/" 
            element={
              <>
                <SEO
                  title="Stop Impulse Spending | The #1 App to Take Control of Your Spending"
                  description="Transform your relationship with money through psychology-backed insights. Take our quiz to get personalized strategies for your spending style."
                  path="/"
                  canonicalUrl="https://impause.com/"
                />
                <Home />
              </>
            } 
          />
          <Route 
            path="/future-you" 
            element={
              <>
                <SEO
                  title="FutureYou | Visualize Your Financial Future"
                  description="Experience an interactive journey to visualize your financial future and understand how today's decisions shape tomorrow's outcomes."
                  path="/future-you"
                  canonicalUrl="https://impause.com/future-you"
                />
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <FutureYou />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/thank-you" 
            element={
              <>
                <SEO
                  title="Thank You | Your Journey to Better Financial Habits Begins"
                  description="Thank you for connecting with impause. Your journey to better financial habits and mindful spending starts now."
                  path="/thank-you"
                  canonicalUrl="https://impause.com/thank-you"
                />
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <ThankYou />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/contact" 
            element={
              <>
                <SEO
                  title="Contact Us | Get Support for Your Financial Journey"
                  description="Have questions about controlling impulse spending or improving your financial habits? Contact the impause team for support."
                  path="/contact"
                  canonicalUrl="https://impause.com/contact"
                />
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <Contact />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/privacy-policy" 
            element={
              <>
                <SEO
                  title="Privacy Policy | How We Protect Your Information"
                  description="Learn how impause protects your personal information and privacy while helping you build better financial habits."
                  path="/privacy-policy"
                  canonicalUrl="https://impause.com/privacy-policy"
                />
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <PrivacyPolicy />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/tools" 
            element={
              <>
                <SEO
                  title="Financial Tools | Resources to Control Impulse Spending"
                  description="Access our suite of behavioral finance tools designed to help you control impulse spending and build healthier financial habits."
                  path="/tools"
                  canonicalUrl="https://impause.com/tools"
                />
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <Tools />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/quiz" 
            element={
              <>
                <SEO
                  title="Financial Behavior Quiz | Understand Your Spending Habits"
                  description="Take our psychology-based quiz to understand your spending triggers and get personalized strategies to control impulse spending."
                  path="/quiz"
                  canonicalUrl="https://impause.com/quiz"
                />
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <Quiz />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog" 
            element={
              <>
                <SEO
                  title="Financial Psychology Blog | Insights on Money Mindset"
                  description="Explore articles on financial psychology, behavioral finance, and mindful spending practices to help you build healthier money habits."
                  path="/blog"
                  canonicalUrl="https://impause.com/blog"
                />
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <Blog />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/psychology-impulse-spending-control" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <PsychologyImpulseSpendingControl />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/fomo-spending-psychology" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <FomoSpendingPsychology />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/personality-type-spending-habits" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <PersonalityTypeSpendingHabits />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/micro-habits-save-money" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <MicroHabitsSaveMoney />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/why-we-impulse-buy-and-how-to-stop" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <WhyWeImpulseBuyAndHowToStop />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/financial-behaviors-literacy" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <FinancialBehaviorsLiteracy />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/best-app-curb-impulse-spending" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <BestAppCurbImpulseSpending />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/health-benefits-budgeting" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <HealthBenefitsBudgeting />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/psychology-of-money-review" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <PsychologyOfMoneyReview />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/what-drives-impulse-spending-research" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <WhatDrivesImpulseSpendingResearch />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/complete-guide-stopping-impulse-spending" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <CompleteGuideStoppingImpulseSpending />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/psychology-impulse-spending-science" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <PsychologyImpulseSpendingScience />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/emotional-triggers-impulse-spending" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <EmotionalTriggersImpulseSpending />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/blog/cbt-techniques-impulse-spending" 
            element={
              <>
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <CBTTechniquesImpulseSpending />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="/structured-data" 
            element={
              <>
                <SEO
                  title="Structured Data | impause"
                  description="Structured data implementation for SEO testing and verification on impause.com"
                  path="/structured-data"
                  canonicalUrl="https://impause.com/structured-data"
                  robotsContent="noindex, nofollow"
                />
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
                  <StructuredDataPage />
                </Suspense>
              </>
            } 
          />
          <Route 
            path="*" 
            element={
              <>
                <SEO
                  title="Page Not Found | impause"
                  description="The page you're looking for cannot be found. Return to impause to continue your journey to better financial habits."
                  path="/404"
                  canonicalUrl="https://impause.com/404"
                />
                <NotFound />
              </>
            } 
          />
        </Routes>
        <Toaster />
      </>
    );
}

function App() {
  // Initialize Google Analytics when app loads
  useEffect(() => {
    // Verify required environment variable is present
    if (!import.meta.env.VITE_GA_MEASUREMENT_ID) {
      console.warn('Missing required Google Analytics key: VITE_GA_MEASUREMENT_ID');
    } else {
      initGA();
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Router />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
